import styled, { keyframes } from 'styled-components'
import { useParams } from 'react-router-dom'
import { formatTokenAmount } from '../../utils/math-utils'
import useRelayer from '../../hooks/useRelayer'
import Address from '../general/Address'
import Network from '../general/Network'

export default function Relayer() {
  const params = useParams()
  const data = useRelayer(params.id)

  const rows = data?.data
    ?.slice()
    .sort((a, b) => (a?.relayer?.id?.toLowerCase() || '').localeCompare(b?.relayer?.id?.toLowerCase() || ''))

  return (
    <Wrap>
      {!data?.data ? (
        <LoaderBar />
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>Address</Th>
                <Th>Network</Th>
                <Th>Balance</Th>
                <Th>Max quota</Th>
                <Th>Quota used</Th>
              </tr>
            </thead>
            <tbody>
              {rows?.map((item, index) => {
                if (!item) return null
                const fmt = (val: any) =>
                  `${Number(formatTokenAmount(val, item.nativeToken?.decimals, { digits: 5 }))} ${item.nativeToken?.symbol || ''}`

                const used = Number(formatTokenAmount(item.quotaUsed, item.nativeToken?.decimals, { digits: 5 }))
                const max = Number(formatTokenAmount(item.maxQuota, item.nativeToken?.decimals, { digits: 5 }))
                const pct = max > 0 ? Math.min((used / max) * 100, 100) : 0

                return (
                  <tr key={index}>
                    <Td>
                      <Address address={item.relayer?.id} chainId={item.chainId} showIdentity={false} />
                    </Td>
                    <Td>
                      <Network network={item.chainId} />
                    </Td>
                    <Td><Value>{fmt(item.balance)}</Value></Td>
                    <Td><Value>{fmt(item.maxQuota)}</Value></Td>
                    <Td>
                      <QuotaCell>
                        <Value>{fmt(item.quotaUsed)}</Value>
                        {max > 0 && (
                          <QuotaBar>
                            <QuotaFill pct={pct} warn={pct > 80} />
                          </QuotaBar>
                        )}
                      </QuotaCell>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Wrap>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const LoaderBar = styled.div`
  height: 2px;
  background: rgba(255,255,255,0.06);
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(111,92,230,0.5), transparent);
    animation: ${shimmer} 1.4s ease infinite;
  }
`

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  color: #a1a1a1;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`

const Td = styled.td`
  padding: 10px 12px;
  color: #a0a0a0;
  font-size: 13px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(255,255,255,0.04);

  tr:last-child & { border-bottom: none; }
`

const Value = styled.span`
  color: #ededed;
  font-variant-numeric: tabular-nums;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
`

const QuotaCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 140px;
`

const QuotaBar = styled.div`
  height: 2px;
  background: rgba(255,255,255,0.06);
  border-radius: 2px;
  overflow: hidden;
`

const QuotaFill = styled.div<{ pct: number; warn: boolean }>`
  height: 100%;
  width: ${({ pct }) => pct}%;
  background: ${({ warn }) => warn ? '#FFCC33' : '#33C2B0'};
  border-radius: 2px;
  transition: width 0.4s ease;
`
